// Card 39, the border band (R548, ui-map-interface.md §17.3).
//
// The classic chart frame: a band of divisions round the four edges of the
// sheet, one division per CELL, with a figure every fifth: the longitude of
// the column or the latitude of the row in the chart frame (R2742), the
// figures the graticule prints. It is the piece
// that makes a screen a sheet. A reader counts divisions along an edge to a
// mark and knows how many cells away it is without measuring anything.
//
// The historical reading fills the divisions dark and light in turn and
// carries an outer rule; the modern one draws a tick per division — a full
// one on a numbered cell, a half one between.
//
// **It is LIVE.** A division is one cell wide on the screen, so the whole
// band re-lays on every frame the camera moves.
//
// **Under `zoom_min` it waits.** Below half scale a cell is under twenty
// screen px and the divisions read as noise rather than as a scale.
//
// **It owns `ctx.bandOn`.** The controller answers this piece's gate first
// and hands the answer to the graticule (card 05), which then draws no tick
// and moves its figures inside the band.
//
// Every strip and every fill is a butt-capped SEGMENT and not a quad: a
// segment carries its own antialiased edge, and the band's edge is the one
// straight line a reader's eye follows all the way round the sheet.

import { F_SCREEN } from '../../layer';
import type { FrameInfo } from '../../layer';
import { COL_PITCH, HEX_SIZE } from '../../../gen/balance';
import { colLon, latFig, lonFig, rowLat } from '../../../ui/chartpos';
import type { ChartContext, ChartSinks, Piece } from '../context';
import type { ChartFace } from '../../gfx/atlas';
import type { RunOpts } from '../glyphs';
import { stepPx } from '../steps';
import { CHART_INK, ROW_PITCH, colLine, faceOf, rowY } from './shared';

export class BorderBand implements Piece {
  readonly id = 'border_band' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = ['reading'] as const;

  // The frame in hand, which `strip` and `put` read; `emit` writes it, so a
  // frame makes no closure.
  private sinks!: ChartSinks;
  private ctx!: ChartContext;
  private x0 = 0;
  private y0 = 0;
  private z = 1;
  private face: ChartFace = 'mono';
  private size = 0;
  private figAlpha = 0;
  /** The options of a figure run, rewritten per figure. */
  private readonly opts: RunOpts = { anchor: 'centre', screen: true, zoom: 1 };

  rebuild(_ctx: ChartContext): void { /* the camera is the whole state */ }

  /** One butt-capped strip between two points of the screen. */
  private strip(
    x0: number, y0: number, x1: number, y1: number,
    width: number, tint: number, alpha: number,
  ): void {
    const z = this.z;
    this.sinks.segments.line(
      this.x0 + x0 / z, this.y0 + y0 / z, this.x0 + x1 / z, this.y0 + y1 / z, width, tint, alpha, F_SCREEN,
    );
  }

  /** One figure at a point of the screen; a blank figure writes nothing. */
  private put(text: string, sx: number, sy: number, anchor: 'left' | 'centre' | 'right'): void {
    if (text === '') return;
    const o = this.opts;
    o.anchor = anchor; o.zoom = this.z;
    this.ctx.glyphs.run(
      this.sinks.quads, text, this.x0 + sx / this.z, this.y0 + sy / this.z,
      this.face, this.size, CHART_INK.pale, this.figAlpha, o,
    );
  }

  /** Four strips that close a rectangle in screen px. */
  private rect(x0: number, y0: number, x1: number, y1: number, width: number, alpha: number): void {
    this.strip(x0, y0, x1, y0, width, CHART_INK.pale, alpha);
    this.strip(x0, y1, x1, y1, width, CHART_INK.pale, alpha);
    this.strip(x0, y0, x0, y1, width, CHART_INK.pale, alpha);
    this.strip(x1, y0, x1, y1, width, CHART_INK.pale, alpha);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.border_band;
    const f = t.fig;
    const r = t[ctx.reading];
    const rf = r.fig;
    // The two readings hold different figures, so each branch reads its own
    // table rather than the union of the two.
    const hf = t.historical.fig;
    const mf = t.modern.fig;
    const hist = ctx.reading === 'historical';
    const z = ctx.zoom;
    const w = ctx.cam.w, h = ctx.cam.h;
    const T = f.band_px;
    this.sinks = sinks; this.ctx = ctx;
    this.x0 = ctx.view.x0; this.y0 = ctx.view.y0; this.z = z;

    // The ground of the four strips.
    this.strip(0, T / 2, w, T / 2, T, CHART_INK.deep, f.band_alpha);
    this.strip(0, h - T / 2, w, h - T / 2, T, CHART_INK.deep, f.band_alpha);
    this.strip(T / 2, 0, T / 2, h, T, CHART_INK.deep, f.band_alpha);
    this.strip(w - T / 2, 0, w - T / 2, h, T, CHART_INK.deep, f.band_alpha);

    this.face = faceOf(ctx.reading);
    // A margin word holds one screen size, so it takes its step's size and
    // no band (R1796).
    this.size = stepPx(this.face, t.text.main);
    this.figAlpha = f.figure_alpha;

    // The divisions along the top and the bottom, one per cell column.
    const colPx = COL_PITCH * z;
    const c0 = Math.floor(ctx.view.x0 / COL_PITCH);
    const c1 = Math.ceil(ctx.view.x1 / COL_PITCH);
    for (let c = c0; c <= c1; c++) {
      const sx = (colLine(c) - ctx.view.x0) * z;
      if (sx > w) break;
      if (hist) {
        const a = c % 2 ? hf.fill_dark : hf.fill_light;
        const x0 = Math.max(0, sx), x1 = Math.min(w, sx + colPx);
        if (x1 > x0) {
          this.strip(x0, T / 2, x1, T / 2, T, CHART_INK.pale, a);
          this.strip(x0, h - T / 2, x1, h - T / 2, T, CHART_INK.pale, a);
        }
      } else if (sx >= 0) {
        const len = c % f.numeral_every === 0 ? T * mf.tick_frac : T * mf.half_tick_frac;
        this.strip(sx, 0, sx, len, r.width, CHART_INK.pale, f.band_alpha);
        this.strip(sx, h, sx, h - len, r.width, CHART_INK.pale, f.band_alpha);
      }
      if (c % f.numeral_every !== 0) continue;
      if (sx <= f.edge_margin || sx >= w - f.edge_margin) continue;
      const num = lonFig(colLon(c));
      this.put(num, sx, T + rf.numeral_dy, 'centre');
      this.put(num, sx, h - T - f.numeral_dy_bottom, 'centre');
    }

    // The divisions along the two sides, one per cell row.
    const rowPx = ROW_PITCH * z;
    const r0 = Math.floor((ctx.view.y0 - HEX_SIZE) / ROW_PITCH);
    const r1 = Math.ceil((ctx.view.y1 - HEX_SIZE) / ROW_PITCH);
    for (let row = r0; row <= r1; row++) {
      const sy = (rowY(row) - ctx.view.y0) * z;
      if (sy > h) break;
      if (hist) {
        const a = row % 2 ? hf.fill_dark : hf.fill_light;
        const y0 = Math.max(0, sy), y1 = Math.min(h, sy + rowPx);
        if (y1 > y0) {
          this.strip(T / 2, y0, T / 2, y1, T, CHART_INK.pale, a);
          this.strip(w - T / 2, y0, w - T / 2, y1, T, CHART_INK.pale, a);
        }
      } else if (sy >= 0) {
        const len = row % f.numeral_every === 0 ? T * mf.tick_frac : T * mf.half_tick_frac;
        this.strip(0, sy, len, sy, r.width, CHART_INK.pale, f.band_alpha);
        this.strip(w, sy, w - len, sy, r.width, CHART_INK.pale, f.band_alpha);
      }
      if (row % f.numeral_every !== 0) continue;
      if (sy <= f.edge_margin || sy >= h - f.edge_margin) continue;
      const num = latFig(rowLat(row));
      this.put(num, T + f.numeral_dx, sy + f.numeral_dy_side, 'left');
      this.put(num, w - T - f.numeral_dx, sy + f.numeral_dy_side, 'right');
    }

    // The inner rule that closes the band, and the outer rule the
    // historical sheet carries round the whole plate.
    this.rect(T, T, w - T, h - T, r.width, f.inner_rule_alpha);
    if (hist) this.rect(0, 0, w, h, r.width, hf.outer_rule_alpha);
  }
}

/** The one instance the registry holds. */
export const borderBand = new BorderBand();
